import { Link, useParams } from "react-router-dom";
import { findUseCase } from "@/data/useCases";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Sparkles, HelpCircle } from "lucide-react";
import { useEffect } from "react";

export default function UseCase({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const uc = findUseCase(slug);

  useSeo({
    title: uc ? uc.metaTitle : "Cas d'usage introuvable",
    description: uc?.metaDescription || "",
    canonicalPath: `/${slug}`,
  });

  // Inject FAQ JSON-LD for rich snippet
  useEffect(() => {
    if (!uc) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-usecase", uc.slug);
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: uc.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head
        .querySelectorAll(`script[data-usecase="${uc.slug}"]`)
        .forEach((el) => el.remove());
    };
  }, [uc]);

  if (!uc) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-[#0F2137]">Cas d'usage introuvable</h1>
        <Link to="/">
          <Button className="mt-4">Accueil</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14" data-testid={`usecase-${uc.slug}`}>
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 font-mono-tech uppercase tracking-wider text-[11px]">
        Beta gratuite
      </Badge>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F2137] mt-4 leading-tight tracking-tight">
        {uc.h1}
      </h1>
      <p className="text-lg text-slate-600 mt-4 max-w-2xl">{uc.heroPunch}</p>
      <p className="text-base text-slate-700 mt-4 max-w-2xl leading-relaxed">{uc.intro}</p>

      <div className="mt-8">
        <Link to="/nouveau">
          <Button className="h-12 px-7 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold">
            <Sparkles className="w-4 h-4 mr-1.5" />
            Générer mon planning gratuitement
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {uc.benefits.map((b, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="w-10 h-10 rounded-lg bg-[#0F2137] flex items-center justify-center mb-3">
              <Check className="w-5 h-5 text-[#F59E0B]" />
            </div>
            <h3 className="font-display font-semibold text-[#0F2137]">{b.title}</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0F2137]">Comment ça marche</h2>
        <div className="mt-6 space-y-3">
          {uc.steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <span className="w-8 h-8 rounded-full bg-[#F59E0B] text-[#0F2137] font-bold flex items-center justify-center shrink-0 font-mono-tech">
                {i + 1}
              </span>
              <span className="text-slate-700 pt-1">{s}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0F2137] flex items-center gap-2">
          <HelpCircle className="w-6 h-6 text-[#F59E0B]" /> Questions fréquentes
        </h2>
        <div className="mt-6 space-y-4">
          {uc.faq.map((f, i) => (
            <details
              key={i}
              className="rounded-lg border border-slate-200 bg-white p-5 group open:border-[#F59E0B]"
            >
              <summary className="font-semibold text-[#0F2137] cursor-pointer list-none flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-[#F59E0B] group-open:rotate-45 transition-transform text-xl leading-none">
                  +
                </span>
              </summary>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-16 rounded-2xl bg-[#0F2137] text-white p-8 sm:p-10 text-center">
        <div className="font-display text-2xl sm:text-3xl font-bold">Prêt à essayer ?</div>
        <p className="text-slate-300 mt-2 max-w-md mx-auto">
          Gratuit pendant la beta, aucune inscription. Vous obtenez votre planning en moins d'une minute.
        </p>
        <Link to="/nouveau">
          <Button className="mt-6 h-12 bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold px-8">
            Créer mon planning
          </Button>
        </Link>
      </div>
    </div>
  );
}
